import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../Config';

const PlanCuentaForm = () => {
    const { id } = useParams();
    const isEditing = !!id;
    const navigate = useNavigate();

    const [form, setForm] = useState({
        codigoIncoop: '',
        nombreCuenta: '',
        tipoCuenta: '',
        nivel: '',
        esImputable: false,
        monedaId: ''
    });
    const [monedas, setMonedas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchMonedas();
        if (isEditing) fetchPlanCuenta();
    }, []);

    const fetchMonedas = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/monedas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error();
            const data = await response.json();
            setMonedas(Array.isArray(data) ? data : []);
        } catch {
            setMonedas([]);
        }
    };

    const fetchPlanCuenta = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/plan-cuentas/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setForm({
                codigoIncoop: data.codigoIncoop,
                nombreCuenta: data.nombreCuenta,
                tipoCuenta: data.tipoCuenta,
                nivel: data.nivel,
                esImputable: data.esImputable ?? false,
                monedaId: data.moneda?.id ?? ''
            });
        } catch (error) {
            Swal.fire('Error', 'No se pudo cargar el plan de cuenta', 'error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.codigoIncoop.trim() || !form.nombreCuenta.trim() || !form.tipoCuenta || !form.nivel) {
            Swal.fire('Atención', 'Código, nombre, tipo y nivel son obligatorios', 'warning');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const url = isEditing
                ? `${API_BASE_URL}/plan-cuentas/${id}`
                : `${API_BASE_URL}/plan-cuentas`;
            const method = isEditing ? 'PUT' : 'POST';

            const body = {
                codigoIncoop: form.codigoIncoop,
                nombreCuenta: form.nombreCuenta,
                tipoCuenta: form.tipoCuenta,
                nivel: Number(form.nivel),
                esImputable: form.esImputable,
                moneda: form.monedaId ? { id: Number(form.monedaId) } : null
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) throw new Error('Error al guardar el plan de cuenta');

            Swal.fire('Éxito', `Plan de cuenta ${isEditing ? 'actualizado' : 'creado'} correctamente`, 'success');
            navigate('/parametrizacion/plan-cuentas');
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-5 px-lg-5" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div className="mb-4">
                <h2 className="fw-bolder text-dark mb-1">{isEditing ? 'Editar Plan de Cuenta' : 'Nuevo Plan de Cuenta'}</h2>
                <p className="text-muted mb-0">Parametrización del sistema</p>
            </div>

            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white" style={{ maxWidth: '600px' }}>
                <form onSubmit={handleSubmit}>
                    <div className="row g-3 mb-3">
                        <div className="col-md-5">
                            <label className="form-label fw-bold">Código INCOOP <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Ej: 2.1.01"
                                value={form.codigoIncoop}
                                onChange={(e) => setForm({ ...form, codigoIncoop: e.target.value })}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Tipo <span className="text-danger">*</span></label>
                            <select
                                className="form-select rounded-pill"
                                value={form.tipoCuenta}
                                onChange={(e) => setForm({ ...form, tipoCuenta: e.target.value })}
                            >
                                <option value="">Seleccionar...</option>
                                <option value="A">Activo</option>
                                <option value="P">Pasivo</option>
                                <option value="I">Ingreso</option>
                                <option value="E">Egreso</option>
                                <option value="O">Otro</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Nivel <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className="form-control rounded-pill"
                                placeholder="Ej: 1, 2, 3"
                                min={1}
                                value={form.nivel}
                                onChange={(e) => setForm({ ...form, nivel: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-bold">Nombre de Cuenta <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className="form-control rounded-pill"
                            placeholder="Nombre de la cuenta contable"
                            value={form.nombreCuenta}
                            onChange={(e) => setForm({ ...form, nombreCuenta: e.target.value })}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-bold">Moneda <span className="text-muted fw-normal">(opcional)</span></label>
                        <select
                            className="form-select rounded-pill"
                            value={form.monedaId}
                            onChange={(e) => setForm({ ...form, monedaId: e.target.value })}
                        >
                            <option value="">Sin moneda</option>
                            {monedas.map(m => (
                                <option key={m.id} value={m.id}>{m.nombre} ({m.codigoIso})</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-4">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="esImputableSwitch"
                                checked={form.esImputable}
                                onChange={(e) => setForm({ ...form, esImputable: e.target.checked })}
                            />
                            <label className="form-check-label fw-bold" htmlFor="esImputableSwitch">
                                {form.esImputable ? 'Es imputable' : 'No es imputable'}
                            </label>
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <button
                            type="submit"
                            className="btn btn-primary rounded-pill fw-bold px-4"
                            disabled={loading}
                        >
                            {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
                            {isEditing ? 'Actualizar' : 'Guardar'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate('/parametrizacion/plan-cuentas')}
                        >
                            Cancelar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PlanCuentaForm;